import { EntityDataModel } from "./EntityDataModel.mjs";
import { JsonPatchUtilities } from "@hds/json-patch-utils";

//TODO Some features of JSON Patch cannot be generated using JsonPatchUtilities.generatePatch()
// One example is the append to array operation: `{ "op": "add", "value":"thing", "path": "/anArray/-" }`
// At the moment an Entity determines the patch by comparing the virtualDataModel to the synchronized data model
// This generated patch is what is used by the `patch()` method to patch the Entity in the system
// In order to support more complex scenarios like this one we need to alter the Entity class to:
//      - keep tabs of the current synchronized state (a.k.a current "known" system representation of the document)
//      - keep tabs of the current virtual state (a.k.a current state in of the document in memory)
//      - keep track of the patch operation that took us from the synchronized state to the virtual data model state
class Entity {
    _lastSyncedAt = undefined;
    _synchronizedDataModel = undefined;//Current "known" system State(document)
    _virtualDataModel;//Current in memory State(document)
    _entityPersistence;
    _options = {
        autoSynchronization: true
    }
    constructor(entityPersistence, seedData, options = {}) {
        // super();
        this._virtualDataModel = new EntityDataModel();
        this._entityPersistence = entityPersistence;
        if (seedData) {
            this._virtualDataModel.fromJSON(seedData);
        }
        this._options = {
            ...this._options,
            ...options
        };
    }
    /*****************************************************/
    // START VIRTUAL DATA MODEL
    /*****************************************************/
    get id() {
        return this._virtualDataModel.id;
    }
    set id(entityId) {
        this._virtualDataModel.id = entityId;
    }
    get __lastOperationId() {
        return this._virtualDataModel.getProperty('__lastOperationId');
    }
    get __toBeArchived() {
        return this._virtualDataModel.getProperty('__toBeArchived');
    }
    markToBeArchived() {
        return this._virtualDataModel.setProperty('__toBeArchived', true);
    }
    toString() {
        return this._virtualDataModel.toString();
    }
    toJSON() {
        return this._virtualDataModel.toJSON();
    }
    reset() {
        return this._virtualDataModel.resetProperties();
    }
    fromJSON(obj) {
        return this._virtualDataModel.fromJSON(obj);
    }
    replaceFromJSON(obj) {
        return this._virtualDataModel.replaceFromJSON(obj);
    }
    get options() {
        return structuredClone(this._options);
    }
    /*****************************************************/
    // END VIRTUAL DATA MODEL
    /* START SYNCHRONIZATION HANDLING */
    /*****************************************************/
    resetToLastSynchronized() {
        if (this._synchronizedDataModel === undefined) {
            this._virtualDataModel.resetProperties();
        }
        else {
            this._virtualDataModel.replaceFromJSON(this._synchronizedDataModel.toJSON());
        }
    }
    _synchronizeFromDocument(document) {
        this._virtualDataModel.replaceFromJSON(document);
        return this._synchronize();
    }
    _synchronize() {
        this._lastSyncedAt = new Date();
        this._synchronizedDataModel = this._virtualDataModel.clone();
    }
    get lastSyncedAt() {
        return this._lastSyncedAt;
    }
    get pendingPatch() {
        const synchronizedProperties = this?._synchronizedDataModel?.properties || {};
        const virtualProperties = this._virtualDataModel.properties;
        const jsonPatch = JsonPatchUtilities.generatePatch(synchronizedProperties, virtualProperties);
        return jsonPatch;
    }
    hasPendingChanges() {
        if (this.pendingPatch?.length !== 0) {
            return true;
        }
        else {
            return false;
        }
    }
    /*****************************************************/
    /* END SYNCHRONIZATION HANDLING */
    // START BUSINESS RULES
    /*****************************************************/
    /**
     * Create is used when wanting to create a new Entity in the system using 
     * the property values in _virtualDataModel.
     * 
     * @note The id property is optional. If not supplied an id is generated in the system.
     * @note This Entity instance will be transformed into the created Entity.
     * @returns {EntityDataModel} EntityDataModel of the created entity
     */
    async create() {
        const createdEdm = await this._entityPersistence.create(this._virtualDataModel);
        if (this._options.autoSynchronization === true) {
            this._synchronize();
        }
        return createdEdm;
    }
    /**
     * Clone will clone the entity identified by the current id property.
     * 
     * @note The entityIdOfTheClone argument is optional. If it is NOT provided an id will be generated for the cloned entity.
     * @note This Entity instance will be transformed into the cloned Entity. Meaning the id property will be changed to `entityIdOfTheClone`
     * @returns {EntityDataModel} EntityDataModel of the cloned entity
     */
    async clone(entityIdOfTheClone) {
        const clonedEdm = await this._entityPersistence.clone(this._virtualDataModel, entityIdOfTheClone);
        if (this._options.autoSynchronization === true) {
            this._synchronize();
        }
        return clonedEdm;
    }
    /**
     * Upsert will create a new Entity in the system or update an existing entity
     * using the property values in _virtualDataModel.
     *
     * @note The id property is optional. If not supplied an entity is created in the system with a generated id.
     * @note This Entity instance will be transformed into the upserted Entity.
     * @returns {EntityDataModel} EntityDataModel of the upserted entity
     */
    async upsert() {
        const upsertedEdm = await this._entityPersistence.upsert(this._virtualDataModel);
        if (this._options.autoSynchronization === true) {
            this._synchronize();
        }
        return upsertedEdm;
    }
    /**
     * Update will attempt to update the entity in the system identified by the id property.
     * 
     * By update it will overwrite the entity in the system with the property values in _virtualDataModel. 
     * Any additional properties already set in the entity in the system will be left alone.
     * 
     * @note If no id property is set this will throw an error.
     * @note This Entity instance will be transformed into the updated Entity. Meaning the id property will be changed to `entityIdToUpdate`
     * @returns {EntityDataModel} EntityDataModel of the updated entity
     */
    async update() {
        const updatedEdm = await this._entityPersistence.update(this._virtualDataModel);
        if (this._options.autoSynchronization === true) {
            this._synchronize();
        }
        return updatedEdm;
    }
    //TODO DEPRECATE THIS METHOD OR ADD FULL OPERATIONAL TRANSFORM SUPPORT
    /**
     * @note If no id property is set this will throw an error.
     * @note This Entity instance will be transformed into the patched Entity.
     * @returns {EntityDataModel} EntityDataModel of the patched entity
     */
    async patch() {
        const patchedEdm = await this._entityPersistence.patch(this._virtualDataModel, this.pendingPatch);
        if (this._options.autoSynchronization === true) {
            this._synchronize();
        }
        return patchedEdm;
    }
    //This will stomp on any outstanding changes in _virtualDataModel
    async patchAndSave(jsonPatch) {
        const patchedEdm = await this._entityPersistence.patch(this._virtualDataModel, jsonPatch);
        if (this._options.autoSynchronization === true) {
            this._synchronize();
        }
        return patchedEdm;
    }
    async appendAndSave(pathToArray, arrayElement) {
        const patchedEdm = await this._entityPersistence.append(this._virtualDataModel, pathToArray, arrayElement);
        if (this._options.autoSynchronization === true) {
            this._synchronize();
        }
        return patchedEdm;
    }
    /**
     * Replace will replace the entire entity with the property values in _virtualDataModel. 
     * Any additional properties already set in the entity in the system will be removed. 
     * 
     * @note If no id property is set this will throw an error.
     * @note This Entity instance will be transformed into the replaced Entity. Meaning the id property will be changed to `entityIdToReplace`
     * @returns {EntityDataModel} EntityDataModel of the replaced entity
     */
    async replace(entityIdToReplace = this.id) {
        const replacedEdm = await this._entityPersistence.replace(this._virtualDataModel, entityIdToReplace);
        if (this._options.autoSynchronization === true) {
            this._synchronize();
        }
        return replacedEdm;
    }
    /**
     * Retrieve a specific entity in the system by ID.
     * @note This Entity instance will be transformed into the retrieved Entity.
     * @returns {EntityDataModel} EntityDataModel of the retrieved entity
     */
    async retrieve(projection = undefined) {
        const retrievedEdm = await this._entityPersistence.retrieve(this._virtualDataModel, projection);
        if (this._options.autoSynchronization === true) {
            this._synchronize();
        }
        return retrievedEdm;
    }
    /**
     * @typedef {Object} DeleteResponse
     * @property {Integer} deletedCount - The number of entities deleted.
     */
    /**
     * Delete will delete the Entity with a given ID in the system.
     * 
     * @note The _lastSyncedAt and _synchronizedDataModel will be set to undefined denoting this entity is no longer in the system.
     * @returns {DeleteResponse}
     */
    async delete() {
        const deletedResults = await this._entityPersistence.delete(this._virtualDataModel);
        if (deletedResults?.deletedCount === 1) {
            if (this._options.autoSynchronization === true) {
                this._lastSyncedAt = undefined;
                this._synchronizedDataModel = undefined;
            }
            return true;
        }
        else {
            return false;
        }
    }
    /*****************************************************/
    // END BUSINESS RULES
    // START RELATIONSHIPS (FUTURE)
    /*****************************************************/
    //TODO FUTURE: Entity Relationships
    /*****************************************************/
    // START ACTIONS (FUTURE)
    /*****************************************************/
    //TODO FUTURE:  Entity Actions
    /*****************************************************/
    // START JOBS (FUTURE)
    /*****************************************************/
    //TODO FUTURE:  Entity Jobs
}
export { Entity };
export default Entity;